import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Coins, 
  HardDrive, 
  Brain, 
  FileSearch, 
  FileText,
  Loader2,
  History,
  AlertTriangle,
  CreditCard,
  DollarSign,
  Zap,
  Check,
  Users,
  UserPlus
} from 'lucide-react';
import { toast } from 'sonner';
import { triggerCreditUpdate } from '../components/CreditIndicator';

const PRICE_PER_CREDIT = 0.20;
const PRICE_PER_SEAT = 2.99;

const BillingCreditsPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [purchasingSeats, setPurchasingSeats] = useState(false);
  const [balanceData, setBalanceData] = useState(null);
  const [seatData, setSeatData] = useState(null);
  const [history, setHistory] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [creditsToBuy, setCreditsToBuy] = useState(10);
  const [seatsToBuy, setSeatsToBuy] = useState(1);
  const [activeTab, setActiveTab] = useState('credits');
  
  // Workspace billing settings
  const [workspaceSettings, setWorkspaceSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [workspaceCurrency, setWorkspaceCurrency] = useState('USD');

  useEffect(() => {
    fetchBalanceData();
    fetchSeatData();
    fetchHistory();
    fetchWorkspaceSettings();
  }, []);

  const fetchSeatData = async () => {
    try {
      const response = await api().get('/seats/info');
      setSeatData(response.data);
    } catch (error) {
      console.error('Failed to load seat info');
    }
  };

  const fetchBalanceData = async () => {
    try {
      const response = await api().get('/credits/balance');
      setBalanceData(response.data);
    } catch (error) {
      toast.error('Failed to load credit balance');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await api().get('/credits/history?page=1&limit=20');
      setHistory(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to load history');
    }
  };

  const fetchWorkspaceSettings = async () => {
    try {
      const response = await api().get('/workspace/billing-settings');
      setWorkspaceSettings(response.data);
      setWorkspaceCurrency(response.data.currency || 'USD');
    } catch (error) {
      console.error('Failed to load workspace settings');
    }
  };

  const saveWorkspaceSettings = async () => {
    setSavingSettings(true);
    try {
      await api().put('/workspace/billing-settings', {
        currency: workspaceCurrency
      });
      toast.success('Workspace billing settings saved');
      fetchWorkspaceSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Check for payment status on page load (returning from Stripe/PayPal)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const status = urlParams.get('status');
    const paypalOrderId = urlParams.get('paypal_order_id') || urlParams.get('token');
    
    if (sessionId && status === 'success') {
      pollPaymentStatus(sessionId);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (status === 'cancelled') {
      toast.info('Payment cancelled');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paypalOrderId && status !== 'cancelled') {
      capturePayPalPayment(paypalOrderId);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.info('Payment is being processed. Please check back shortly.');
      return;
    }
    
    try {
      const response = await api().get(`/billing/checkout-status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Credits added to your account.');
        fetchBalanceData();
        fetchHistory();
        triggerCreditUpdate();
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const capturePayPalPayment = async (orderId) => {
    try {
      const response = await api().post(`/billing/paypal/capture/${orderId}`);
      if (response.data.status === 'completed') {
        toast.success('PayPal payment successful!');
        fetchBalanceData();
        fetchHistory();
        triggerCreditUpdate();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process PayPal payment');
    }
  };

  const handlePurchaseCredits = async () => {
    if (creditsToBuy < 1) {
      toast.error('Please enter at least 1 credit');
      return;
    }
    
    setPurchasing(true);
    try {
      const originUrl = window.location.origin;
      const totalPrice = (creditsToBuy * PRICE_PER_CREDIT).toFixed(2);
      
      if (paymentMethod === 'stripe') {
        const response = await api().post('/billing/create-checkout', {
          package_type: 'credits_custom',
          credits: creditsToBuy,
          amount_usd: parseFloat(totalPrice),
          origin_url: originUrl
        });
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        }
      } else if (paymentMethod === 'paypal') {
        const response = await api().post('/billing/paypal/create-order', {
          package_type: 'credits_custom',
          credits: creditsToBuy,
          amount_usd: parseFloat(totalPrice),
          origin_url: originUrl
        });
        if (response.data.approval_url) {
          window.location.href = response.data.approval_url;
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate checkout');
      setPurchasing(false);
    }
  };

  const handlePurchaseSeats = async () => {
    if (seatsToBuy < 1) {
      toast.error('Please enter at least 1 seat');
      return;
    }
    
    setPurchasingSeats(true);
    try {
      const originUrl = window.location.origin;
      
      const response = await api().post('/seats/purchase', {
        seats: seatsToBuy,
        payment_method: paymentMethod,
        origin_url: originUrl
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else if (response.data.approval_url) {
        window.location.href = response.data.approval_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate seat purchase');
      setPurchasingSeats(false);
    }
  };

  const totalPrice = (creditsToBuy * PRICE_PER_CREDIT).toFixed(2);
  const totalSeatPrice = (seatsToBuy * PRICE_PER_SEAT).toFixed(2);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const { credits, storage, ai_operations } = balanceData || {};

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="billing-credits-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Billing & Credits</h1>
        <p className="text-muted-foreground mt-1">Manage your AI credits, team seats, and storage</p>
      </div>

      {/* Balance Overview - 3 columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Credits Balance */}
        <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-amber-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Coins className="w-5 h-5 text-primary" />
              AI Credits
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 mb-2">
              <span className="text-4xl font-bold text-primary">{credits?.balance || 0}</span>
              <span className="text-muted-foreground mb-1">credits</span>
            </div>
            <p className="text-sm text-muted-foreground">
              ${((credits?.balance || 0) * PRICE_PER_CREDIT).toFixed(2)} value
            </p>
            {credits?.balance < 10 && (
              <div className="mt-2 flex items-center gap-2 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-xs">Low credits!</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Team Seats */}
        <Card className="border-blue-500/20 bg-gradient-to-br from-blue-500/5 to-cyan-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="w-5 h-5 text-blue-500" />
              Team Seats
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 mb-2">
              <span className="text-4xl font-bold text-blue-600">{seatData?.used_seats || 1}</span>
              <span className="text-muted-foreground mb-1">/ {seatData?.total_seats || 1}</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {seatData?.free_seats || 1} free + {seatData?.paid_seats || 0} paid
            </p>
            {seatData?.available_seats === 0 && (
              <div className="mt-2 flex items-center gap-2 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-xs">No seats available!</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Storage */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <HardDrive className="w-5 h-5 text-emerald-500" />
              Storage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 mb-2">
              <span className="text-3xl font-bold text-emerald-600">{storage?.used_mb?.toFixed(1) || 0}</span>
              <span className="text-muted-foreground mb-1">/ {storage?.limit_mb || 1024} MB</span>
            </div>
            <Progress 
              value={storage?.used_percentage || 0} 
              className="h-2"
            />
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different billing sections */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="credits" className="flex items-center gap-2">
            <Coins className="w-4 h-4" />
            AI Credits
          </TabsTrigger>
          <TabsTrigger value="seats" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Team Seats
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Currency Settings
          </TabsTrigger>
        </TabsList>

        {/* Credits Tab */}
        <TabsContent value="credits" className="space-y-4">
          {/* Pricing Info */}
          <Card className="border-2 border-dashed">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                AI Operation Pricing
              </CardTitle>
              <CardDescription>Each AI operation costs 1 credit = $0.20</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {ai_operations && Object.entries(ai_operations).map(([key, op]) => (
                  <div key={key} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <div className="p-2 rounded-full bg-primary/10">
                      {key === 'ai_counselor_session' && <Brain className="w-4 h-4 text-primary" />}
                      {key === 'document_vetting' && <FileSearch className="w-4 h-4 text-primary" />}
                      {key === 'profile_extraction' && <FileText className="w-4 h-4 text-primary" />}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{op.name}</p>
                      <p className="text-xs text-muted-foreground">{op.description}</p>
                      <Badge variant="secondary" className="mt-1 text-xs">
                        ${op.cost_usd.toFixed(2)}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Top Up Credits Section */}
          <Card className="border-primary/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Top Up Credits
              </CardTitle>
              <CardDescription>Enter the number of credits you want to buy</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Credits Input */}
              <div className="flex flex-col md:flex-row gap-4 items-end">
                <div className="flex-1 space-y-2">
                  <Label htmlFor="credits">Number of Credits</Label>
                  <Input
                    id="credits"
                    type="number"
                    min="1"
                    value={creditsToBuy}
                    onChange={(e) => setCreditsToBuy(Math.max(1, parseInt(e.target.value) || 1))}
                    className="text-2xl font-bold h-14 text-center"
                  />
                </div>
                <div className="text-center md:text-left pb-2">
                  <span className="text-2xl text-muted-foreground">×</span>
                </div>
                <div className="flex-1 space-y-2">
                  <Label>Price per Credit</Label>
                  <div className="h-14 flex items-center justify-center bg-muted rounded-md text-2xl font-bold">
                    $0.20
                  </div>
                </div>
                <div className="text-center md:text-left pb-2">
                  <span className="text-2xl text-muted-foreground">=</span>
                </div>
                <div className="flex-1 space-y-2">
                  <Label>Total</Label>
                  <div className="h-14 flex items-center justify-center bg-primary/10 rounded-md text-2xl font-bold text-primary">
                    ${totalPrice}
                  </div>
                </div>
              </div>

              {/* Quick Select Buttons */}
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-muted-foreground mr-2">Quick select:</span>
                {[10, 25, 50, 100, 200].map((amount) => (
                  <Button
                    key={amount}
                    variant={creditsToBuy === amount ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCreditsToBuy(amount)}
                  >
                    {amount} (${(amount * PRICE_PER_CREDIT).toFixed(0)})
                  </Button>
                ))}
              </div>

              {/* Payment Method */}
              <div className="space-y-3">
                <Label>Payment Method</Label>
                <div className="flex gap-3">
                  <Button
                    variant={paymentMethod === 'stripe' ? 'default' : 'outline'}
                    className="flex-1 h-12"
                    onClick={() => setPaymentMethod('stripe')}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Stripe
                    {paymentMethod === 'stripe' && <Check className="w-4 h-4 ml-2" />}
                  </Button>
                  <Button
                    variant={paymentMethod === 'paypal' ? 'default' : 'outline'}
                    className="flex-1 h-12"
                    onClick={() => setPaymentMethod('paypal')}
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    PayPal
                    {paymentMethod === 'paypal' && <Check className="w-4 h-4 ml-2" />}
                  </Button>
                </div>
              </div>

              {/* Purchase Button */}
              <Button 
                className="w-full h-14 text-lg"
                onClick={handlePurchaseCredits}
                disabled={purchasing || creditsToBuy < 1}
              >
                {purchasing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Pay ${totalPrice} for {creditsToBuy} Credits
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Seats Tab */}
        <TabsContent value="seats" className="space-y-4">
          {/* Seat Pricing Info */}
          <Card className="border-2 border-dashed">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" />
                Team Seat Pricing
              </CardTitle>
              <CardDescription>Add team members to your organization</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
                  <div className="p-2 rounded-full bg-emerald-100 dark:bg-emerald-900">
                    <UserPlus className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-800 dark:text-emerald-200">First User (Admin)</p>
                    <p className="text-sm text-emerald-600 dark:text-emerald-400">Always FREE</p>
                    <p className="text-xs text-muted-foreground mt-1">Included with every account</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                  <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-blue-800 dark:text-blue-200">Additional Team Members</p>
                    <p className="text-sm text-blue-600 dark:text-blue-400">$2.99 / seat / month</p>
                    <p className="text-xs text-muted-foreground mt-1">Add counselors, staff, and more</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Current Seat Usage */}
          <Card>
            <CardHeader>
              <CardTitle>Current Usage</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                  <span>Free Seats</span>
                  <Badge variant="secondary">{seatData?.free_seats || 1}</Badge>
                </div>
                <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                  <span>Paid Seats</span>
                  <Badge variant="secondary">{seatData?.paid_seats || 0}</Badge>
                </div>
                <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                  <span>Total Seats</span>
                  <Badge>{seatData?.total_seats || 1}</Badge>
                </div>
                <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                  <span>Used Seats</span>
                  <Badge variant={seatData?.available_seats === 0 ? "destructive" : "outline"}>
                    {seatData?.used_seats || 1}
                  </Badge>
                </div>
                <div className="flex justify-between items-center p-3 bg-primary/10 rounded-lg">
                  <span className="font-medium">Available Seats</span>
                  <Badge variant={seatData?.available_seats === 0 ? "destructive" : "default"}>
                    {seatData?.available_seats || 0}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Purchase Seats Section */}
          <Card className="border-blue-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Purchase Additional Seats
              </CardTitle>
              <CardDescription>Add more team members to your organization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Seats Input */}
              <div className="flex flex-col md:flex-row gap-4 items-end">
                <div className="flex-1 space-y-2">
                  <Label htmlFor="seats">Number of Seats</Label>
                  <Input
                    id="seats"
                    type="number"
                    min="1"
                    max="100"
                    value={seatsToBuy}
                    onChange={(e) => setSeatsToBuy(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                    className="text-2xl font-bold h-14 text-center"
                  />
                </div>
                <div className="text-center md:text-left pb-2">
                  <span className="text-2xl text-muted-foreground">×</span>
                </div>
                <div className="flex-1 space-y-2">
                  <Label>Price per Seat/Month</Label>
                  <div className="h-14 flex items-center justify-center bg-muted rounded-md text-2xl font-bold">
                    $2.99
                  </div>
                </div>
                <div className="text-center md:text-left pb-2">
                  <span className="text-2xl text-muted-foreground">=</span>
                </div>
                <div className="flex-1 space-y-2">
                  <Label>Total/Month</Label>
                  <div className="h-14 flex items-center justify-center bg-blue-500/10 rounded-md text-2xl font-bold text-blue-600">
                    ${totalSeatPrice}
                  </div>
                </div>
              </div>

              {/* Quick Select Buttons */}
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-muted-foreground mr-2">Quick select:</span>
                {[1, 3, 5, 10, 20].map((amount) => (
                  <Button
                    key={amount}
                    variant={seatsToBuy === amount ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSeatsToBuy(amount)}
                  >
                    {amount} seat{amount > 1 ? 's' : ''} (${(amount * PRICE_PER_SEAT).toFixed(2)}/mo)
                  </Button>
                ))}
              </div>

              {/* Payment Method */}
              <div className="space-y-3">
                <Label>Payment Method</Label>
                <div className="flex gap-3">
                  <Button
                    variant={paymentMethod === 'stripe' ? 'default' : 'outline'}
                    className="flex-1 h-12"
                    onClick={() => setPaymentMethod('stripe')}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Stripe
                    {paymentMethod === 'stripe' && <Check className="w-4 h-4 ml-2" />}
                  </Button>
                  <Button
                    variant={paymentMethod === 'paypal' ? 'default' : 'outline'}
                    className="flex-1 h-12"
                    onClick={() => setPaymentMethod('paypal')}
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    PayPal
                    {paymentMethod === 'paypal' && <Check className="w-4 h-4 ml-2" />}
                  </Button>
                </div>
              </div>

              {/* Purchase Button */}
              <Button 
                className="w-full h-14 text-lg bg-blue-600 hover:bg-blue-700"
                onClick={handlePurchaseSeats}
                disabled={purchasingSeats || seatsToBuy < 1}
              >
                {purchasingSeats ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-5 h-5 mr-2" />
                    Pay ${totalSeatPrice}/month for {seatsToBuy} Seat{seatsToBuy > 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-500" />
                Workspace Currency Settings
              </CardTitle>
              <CardDescription>
                Set the default currency for your workspace. This will be used across all billing, invoices, and financial reports.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label htmlFor="workspace-currency">Workspace Currency</Label>
                <select
                  id="workspace-currency"
                  value={workspaceCurrency}
                  onChange={(e) => setWorkspaceCurrency(e.target.value)}
                  className="w-full max-w-xs p-3 border rounded-lg bg-background text-foreground"
                >
                  <option value="USD">USD - US Dollar ($)</option>
                  <option value="GBP">GBP - British Pound (£)</option>
                  <option value="EUR">EUR - Euro (€)</option>
                  <option value="PKR">PKR - Pakistani Rupee (Rs)</option>
                  <option value="INR">INR - Indian Rupee (₹)</option>
                  <option value="AED">AED - UAE Dirham (د.إ)</option>
                  <option value="CAD">CAD - Canadian Dollar (C$)</option>
                  <option value="AUD">AUD - Australian Dollar (A$)</option>
                </select>
                <p className="text-sm text-muted-foreground">
                  This currency will be applied to all BPO billing, invoices, and financial reports throughout your workspace.
                </p>
              </div>

              <div className="border-t pt-6">
                <Button 
                  onClick={saveWorkspaceSettings} 
                  disabled={savingSettings}
                  className="min-w-32"
                >
                  {savingSettings ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Save Settings
                    </>
                  )}
                </Button>
              </div>

              {workspaceSettings && (
                <div className="bg-muted/50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Current Settings</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Currency:</span>
                      <span className="ml-2 font-medium">{workspaceSettings.currency || 'USD'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Last Updated:</span>
                      <span className="ml-2 font-medium">
                        {workspaceSettings.updated_at 
                          ? new Date(workspaceSettings.updated_at).toLocaleDateString()
                          : 'Never'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Currency Info */}
          <Card className="border-dashed">
            <CardHeader>
              <CardTitle className="text-lg">How Currency Settings Work</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5" />
                  <span>All new invoices will use the selected currency</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5" />
                  <span>BPO billing formulas will display amounts in this currency</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5" />
                  <span>Financial reports and analytics will use this currency</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5" />
                  <span>Existing invoices will retain their original currency</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Transaction History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Recent Transactions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length > 0 ? (
            <ScrollArea className="h-[300px]">
              <div className="space-y-3">
                {history.map((tx, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${tx.credits_used > 0 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-green-100 dark:bg-green-900/30'}`}>
                        {tx.credits_used > 0 ? (
                          <Zap className="w-4 h-4 text-red-600 dark:text-red-400" />
                        ) : (
                          <Coins className="w-4 h-4 text-green-600 dark:text-green-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{tx.feature || 'Credit Purchase'}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(tx.created_at).toLocaleDateString()} {new Date(tx.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                    <div className={`font-bold ${tx.credits_used > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {tx.credits_used > 0 ? `-${tx.credits_used}` : `+${tx.credits_purchased || 0}`}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No transactions yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingCreditsPage;
